import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useAccountingMetadata } from "../../../hooks/useAccountingMetadata";
import { useUnsavedChanges } from "../../../hooks/useUnsavedChanges";
import { useSyncLogs } from "../../../hooks/useSyncLogs";
import { useAccountingOAuth } from "../../../hooks/useAccountingOAuth";
import { useAccountingConnections } from "../../../hooks/useAccountingConnections";
import { useAccountingForm } from "../../../hooks/useAccountingForm";
import { errorSnackBar } from "../../../actions/common.action";
import Row from 'react-bootstrap/Row';
import Tabs from 'react-bootstrap/Tabs';
import Tab from 'react-bootstrap/Tab';
import CircularIndeterminate from "../../../components/circularLoader";
import ConnectionPlatforms from "./connection-platforms";
import AccountingConfigForm from "./accounting-config-form";
import SyncLogsTable from "./sync-logs-table";
import ManagingOrganisationBanner from "./managing-organisation-banner";
import UnsavedChangesModal from "./unsaved-changes-modal";
import AccountingSyncModals from "./accounting-sync-modals";
import idx from "idx";
import "../scss/templates.scss";
import "../scss/accounting-integrations.scss";

/**
 * Returns the user-friendly display name for a platform identifier.
 * @param {string} platform - Platform key ('xero', 'quickbooks', 'myob')
 * @returns {string} Display name
 */
function getPlatformDisplayName(platform) {
  switch (platform) {
    case 'xero': return 'Xero Accounting';
    case 'quickbooks': return 'QuickBooks Online';
    case 'myob': return 'MYOB Business';
    default: return platform;
  }
}

const AccountingSettings = ({
  writePermission,
  wSupplierPermission,
  wContractorPermission,
  wFinancePermission,
  ...props
}) => {
  const dispatch = useDispatch();
  const user = useSelector(state => state.user.userData);
  const accountId = idx(user, _ => _.selected_account.id);

  const canSyncContacts = Boolean(wSupplierPermission || wContractorPermission);
  const canSyncBills = Boolean(wFinancePermission);
  const canSyncInvoices = Boolean(wFinancePermission);


  const [settingsTab, setSettingsTab] = useState("config");
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [showSyncInvoicesModal, setShowSyncInvoicesModal] = useState(false);
  const [showSyncBillsModal, setShowSyncBillsModal] = useState(false);
  const [syncTargetConnection, setSyncTargetConnection] = useState(null);

  const selectConnectionRef = useRef(() => { });

  const {
    loading,
    connections,
    selectedConnection,
    setSelectedConnection,
    fetchConnections,
    handleDisconnect,
    handleToggleActive,
    handleSetDefault
  } = useAccountingConnections(accountId, (connection) => selectConnectionRef.current(connection));

  const { handleConnect } = useAccountingOAuth(accountId, fetchConnections);

  const {
    logs,
    logsTotalCount,
    logsCurrentPage,
    logsPerPage,
    refreshingLogs,
    logsSearchQuery,
    logsSortBy,
    fetchLogs
  } = useSyncLogs();

  useEffect(() => {
    if (selectedConnection?.id) {
      fetchLogs(selectedConnection.id, 1, logsPerPage, logsSearchQuery, logsSortBy);
    }
  }, [selectedConnection?.id, fetchLogs, logsPerPage, logsSearchQuery, logsSortBy]);

  const {
    accountsList,
    taxRatesList,
    currenciesList,
    invoiceStatusesList,
    poStatusesList,
    itemsList,
    loadingMetadata
  } = useAccountingMetadata(selectedConnection?.id, selectedConnection?.is_expired);

  const onSaveSuccess = useCallback((updatedSettings) => {
    fetchConnections(true, false);
  }, [fetchConnections]);

  const form = useAccountingForm(
    selectedConnection,
    accountsList,
    taxRatesList,
    currenciesList,
    loadingMetadata,
    onSaveSuccess,
    itemsList
  );

  // Populate form inputs and update Redux selectedConnection when card is selected
  selectConnectionRef.current = (conn) => {
    if (conn) {
      setSelectedConnection(conn);
      form.selectConnection(conn);
    }
  };

  const {
    showUnsavedModal,
    performWithConfirmation,
    handleCancel,
    handleDiscard,
    handleSaveAndExit
  } = useUnsavedChanges(form.isDirty, () => form.saveSettings());

  const handleSyncContacts = () => {
    if (!selectedConnection) return;
    performWithConfirmation(() => {
      setSyncTargetConnection(selectedConnection);
      setShowSyncModal(true);
    });
  };

  const handleOpenSyncInvoices = () => {
    if (!selectedConnection) return;
    performWithConfirmation(() => {
      setSyncTargetConnection(selectedConnection);
      setShowSyncInvoicesModal(true);
    });
  };

  const handleOpenSyncBills = () => {
    if (!selectedConnection) return;
    performWithConfirmation(() => {
      setSyncTargetConnection(selectedConnection);
      setShowSyncBillsModal(true);
    });
  };

  const handleSyncConn = (conn) => {
    performWithConfirmation(() => {
      setSyncTargetConnection(conn);
      setShowSyncModal(true);
    });
  };

  const handleSyncSuccess = () => {
    const targetConn = selectedConnection || syncTargetConnection;
    if (targetConn) {
      fetchConnections(false, false);
      fetchLogs(targetConn.id, 1, logsPerPage, logsSearchQuery);
    }
  };

  const entitlements = useSelector((state) => state.user.entitlements) || {};
  const featureFlags = useSelector((state) => state.user.featureFlags) || {};

  const allPlatforms = [
    {
      key: 'xero',
      name: 'Xero Accounting',
      logoText: 'X',
      logoColor: '#13B5EA',
      isSupported: true,
      entitlementKey: 'xeroIntegration',
      flagKey: 'xero_integration'
    },
    {
      key: 'quickbooks',
      name: 'QuickBooks Online',
      logoText: 'Q',
      logoColor: '#2CA01C',
      isSupported: true,
      entitlementKey: 'qboIntegration',
      flagKey: 'quickbooks_integration'
    },
    {
      key: 'myob',
      name: 'MYOB Business',
      logoText: 'M',
      logoColor: '#4A154B',
      isSupported: false,
      entitlementKey: 'myobIntegration',
      flagKey: 'myob_integration'
    }
  ];

  const platforms = allPlatforms.filter(p => {
    const hasEntitlement = entitlements[p.entitlementKey] !== false;
    const hasFlag = featureFlags[p.flagKey] ?? true;
    return hasEntitlement && hasFlag;
  });

  if (loading && connections.length === 0) {
    return <CircularIndeterminate message="Loading integrations..." />;
  }

  const invoiceStatusItems = [
    { value: "DRAFT", key: "Draft" },
    { value: "SUBMITTED", key: "Submitted" },
    { value: "AUTHORISED", key: "Authorised" }
  ];

  return (
    <div className="purchase-order-block accounting-integrations-page" data-cy="accounting-settings-container">
      {/* Upper Grid: Connection Platforms */}
      <ConnectionPlatforms
        platforms={platforms}
        connections={connections}
        selectedConnection={selectedConnection}
        writePermission={writePermission}
        canSyncContacts={canSyncContacts}
        selectConnection={(conn) => performWithConfirmation(() => selectConnectionRef.current(conn))}
        handleConnect={(platform, forceLogin, connectionId) => {
          form.setIsDirty(false);
          handleConnect(platform, forceLogin, connectionId);
        }}
        handleSetDefault={(conn) => performWithConfirmation(() => handleSetDefault(conn))}
        handleToggleActive={(conn, e) => performWithConfirmation(() => handleToggleActive(conn, e))}
        handleSyncConn={handleSyncConn}
        handleDisconnect={(conn) => {
          form.setIsDirty(false);
          handleDisconnect(conn);
        }}
      />

      {/* Selected Connection banner, settings, and logs cards */}
      {selectedConnection && platforms.some(p => p.key === selectedConnection.platform) && (
        <div className="mt-4 pt-4 border-top selected-connection-settings-section">
          {/* Managing Organisation Banner */}
          <ManagingOrganisationBanner
            selectedConnection={selectedConnection}
            platforms={platforms}
            getPlatformDisplayName={getPlatformDisplayName}
          />

          <Tabs
            activeKey={settingsTab}
            onSelect={(k) => performWithConfirmation(() => setSettingsTab(k))}
            className="mb-4 settings-tabs border-bottom"
          >
            <Tab eventKey="config" title="CONFIGURATION">
              <Row>
                {/* Left Column: Integration Settings */}
                <AccountingConfigForm
                  writePermission={writePermission}
                  canSyncContacts={canSyncContacts}
                  canSyncInvoices={canSyncInvoices}
                  canSyncBills={canSyncBills}
                  invoiceStatus={form.invoiceStatus}
                  setInvoiceStatus={form.setInvoiceStatus}
                  purchaseOrderStatus={form.purchaseOrderStatus}
                  setPurchaseOrderStatus={form.setPurchaseOrderStatus}
                  squashLinesInvoices={form.squashLinesInvoices}
                  setSquashLinesInvoices={form.setSquashLinesInvoices}
                  squashLinesBills={form.squashLinesBills}
                  setSquashLinesBills={form.setSquashLinesBills}
                  squashRetention={form.squashRetention}
                  setSquashRetention={form.setSquashRetention}
                  lineAmountTypes={form.lineAmountTypes}
                  setLineAmountTypes={form.setLineAmountTypes}
                  trackingCategoryName={form.trackingCategoryName}
                  setTrackingCategoryName={form.setTrackingCategoryName}
                  defaultCurrency={form.defaultCurrency}
                  setDefaultCurrency={form.setDefaultCurrency}
                  accountCode={form.accountCode}
                  setAccountCode={form.setAccountCode}
                  cogsAccountCode={form.cogsAccountCode}
                  setCogsAccountCode={form.setCogsAccountCode}
                  contractorAccountCode={form.contractorAccountCode}
                  setContractorAccountCode={form.setContractorAccountCode}
                  cogsAccountType={form.cogsAccountType}
                  setCogsAccountType={form.setCogsAccountType}
                  contractorAccountType={form.contractorAccountType}
                  setContractorAccountType={form.setContractorAccountType}
                  taxType={form.taxType}
                  setTaxType={form.setTaxType}
                  expenseTaxType={form.expenseTaxType}
                  setExpenseTaxType={form.setExpenseTaxType}
                  defaultItemId={form.defaultItemId}
                  setDefaultItemId={form.setDefaultItemId}
                  invoiceStatusesList={invoiceStatusesList}
                  poStatusesList={poStatusesList}
                  currenciesList={currenciesList}
                  accountsList={accountsList}
                  taxRatesList={taxRatesList}
                  itemsList={itemsList}
                  invoiceStatusItems={invoiceStatusItems}
                  loadingMetadata={loadingMetadata}
                  selectedConnection={selectedConnection}
                  handleSaveSettings={form.handleSaveSettings}
                  handleSyncContacts={handleSyncContacts}
                  handleOpenSyncInvoices={handleOpenSyncInvoices}
                  handleOpenSyncBills={handleOpenSyncBills}
                  getPlatformDisplayName={getPlatformDisplayName}
                />
              </Row>
            </Tab>
            <Tab eventKey="logs" title="SYNC LOGS">
              <Row>
                <SyncLogsTable
                  key={selectedConnection ? selectedConnection.id : 'no-connection'}
                  selectedConnection={selectedConnection}
                  getPlatformDisplayName={getPlatformDisplayName}
                  refreshingLogs={refreshingLogs}
                  fetchLogs={fetchLogs}
                  logsCurrentPage={logsCurrentPage}
                  logsPerPage={logsPerPage}
                  logsSearchQuery={logsSearchQuery}
                  logsSortBy={logsSortBy}
                  logs={logs}
                  logsTotalCount={logsTotalCount}
                />
              </Row>
            </Tab>
          </Tabs>
        </div>
      )}

      <AccountingSyncModals
        showSyncModal={showSyncModal}
        setShowSyncModal={setShowSyncModal}
        showSyncInvoicesModal={showSyncInvoicesModal}
        setShowSyncInvoicesModal={setShowSyncInvoicesModal}
        showSyncBillsModal={showSyncBillsModal}
        setShowSyncBillsModal={setShowSyncBillsModal}
        syncTargetConnection={syncTargetConnection}
        handleSyncSuccess={handleSyncSuccess}
      />

      <UnsavedChangesModal
        show={showUnsavedModal}
        onCancel={handleCancel}
        onDiscard={() => {
          form.setIsDirty(false);
          handleDiscard();
        }}
        onSaveAndExit={async () => {
          try {
            await handleSaveAndExit();
          } catch (err) {
            dispatch(errorSnackBar(err.message || 'Failed to update settings'));
          }
        }}
      />
    </div>
  );
};

export default AccountingSettings;
